import { useCallback, useMemo, useRef, useState } from 'react';
import { Breadcrumb } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import CreatePlanModal from '../components/CreatePlanModal';
import { type PlanRecord } from '../types/plan';
import PlansIndexTable, { type PlansIndexTableHandle } from './PlansIndexTable';
import PlanIndexToolbar from './PlanIndexToolbar';
import { Link } from 'react-router-dom';
import { useTpoGenConfig } from '../context/CustomerConfigContext';
import { transformFiltersV2 } from 'utils/helperMethods';
import { usePlanIndexUrlState } from './hooks/ui/usePlanIndexUrlState';

dayjs.extend(relativeTime);

const PlanIndex = () => {
    const tpoGenConfig = useTpoGenConfig();
    const { planIndexFilterKeys } = tpoGenConfig;
    const plansTableRef = useRef<PlansIndexTableHandle>(null);
    const [createPlanModalOpen, setCreatePlanModalOpen] = useState(false);
    const [createPlanGroupDetails, setCreatePlanGroupDetails] = useState<PlanRecord | null>(null);
    const [editPlanDetails, setEditPlanDetails] = useState<PlanRecord | null>(null);

    const {
        searchKeyword,
        setSearchKeyword,
        showMyPlans,
        setShowMyPlans,
        selectedColumns,
        setSelectedColumns,
        allCheckedAttribute,
        allSelectedValueForAllAttribute,
        applyFilterObject,
        setAppliedFilters,
        resetPlanIndexFilters,
    } = usePlanIndexUrlState();

    const filters = useMemo(
        () => transformFiltersV2(applyFilterObject, planIndexFilterKeys),
        [applyFilterObject, planIndexFilterKeys],
    );

    const onCreateNewPlanClick = useCallback(() => {
        setEditPlanDetails(null);
        setCreatePlanGroupDetails(null);
        setCreatePlanModalOpen(true);
    }, []);

    return (
        <div>
            <div className="mb-3">
                <Breadcrumb>
                    <Breadcrumb.Item>
                        <Link to="/tpo">{tpoGenConfig.labels.common.breadcrumbRoot}</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>{tpoGenConfig.labels.planIndex.breadcrumb}</Breadcrumb.Item>
                </Breadcrumb>
            </div>
            <div className="flex flex-col px-[1.5rem] bg-[#FFF] py-[1.75rem] rounded-[0.25rem] border-[1] border-[#EAEAEC]">
                <CreatePlanModal
                    open={createPlanModalOpen}
                    onCancel={() => {
                        setCreatePlanModalOpen(false);
                        setCreatePlanGroupDetails(null);
                        setEditPlanDetails(null);
                    }}
                    onSuccessHandler={() => plansTableRef.current?.refreshExpandedGroups()}
                    createMode={createPlanGroupDetails ? 'planning-group-create' : 'plan-create'}
                    planningGroupDetails={createPlanGroupDetails}
                    editPlanDetails={editPlanDetails}
                />
                <PlanIndexToolbar
                    searchKeyword={searchKeyword}
                    setSearchKeyword={setSearchKeyword}
                    showMyPlans={showMyPlans}
                    setShowMyPlans={setShowMyPlans}
                    selectedColumns={selectedColumns}
                    setSelectedColumns={setSelectedColumns}
                    allCheckedAttribute={allCheckedAttribute}
                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                    applyFilterObject={applyFilterObject}
                    setAppliedFilters={setAppliedFilters}
                    resetPlanIndexFilters={resetPlanIndexFilters}
                    onCreateNewPlanClick={onCreateNewPlanClick}
                />

                <PlansIndexTable
                    ref={plansTableRef}
                    searchKeyword={searchKeyword}
                    selectedColumns={selectedColumns}
                    showMyPlans={showMyPlans}
                    filters={filters}
                    setCreatePlanGroupDetails={setCreatePlanGroupDetails}
                    setCreatePlanModalOpen={setCreatePlanModalOpen}
                    setEditPlanDetails={setEditPlanDetails}
                />
            </div>
        </div>
    );
};

export default PlanIndex;
